import React, { useState, useEffect, useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useProducts } from '../../../hooks/useProducts.jsx';
import { useRefreshHubSessionOnMount } from '../../../hooks/useRefreshHubSessionOnMount.js';
import { buildApiUrl, freshFetchInit } from '../../../utils/apiBase.js';
import { getEffectiveLaunchUrl } from '../../../utils/hubEnv.js';
import { useBeta } from '../../../contexts/BetaContext.jsx';
import { useClinicPaymentMethodStatus } from '../../../hooks/useClinicPaymentMethodStatus.js';
import {
  buildActiveLicenseKeySet,
  findContractForProduct,
  isProductLicensed,
  isContractLaunchable,
  pickPrimaryContractSummary,
} from '../../../utils/licenseUtils.js';
import { getNextBillingAnchorDate } from '../../../utils/jstDateHelper.js';

/**
 * ダッシュボード
 */
const Dashboard = () => {
  const { user, clinic, licenses, isLaunching = false, onLaunchStart } = useOutletContext();
  const canManageLicenses = user?.role === 'doctor' || user?.role === 'admin';
  const { betaFeaturesRestricted } = useBeta();
  const { hasPaymentMethod } = useClinicPaymentMethodStatus({ enabled: canManageLicenses });
  const [clinicUsers, setClinicUsers] = useState([]);
  const [contracts, setContracts] = useState([]);

  const { products, loading: productsLoading } = useProducts({ freshOnMount: true });
  useRefreshHubSessionOnMount();

  useEffect(() => {
    // 登録ユーザー数の表示に使う一覧。読取は権限不問（同一クリニックの全ロール）。
    // サーバ側 GET /api/clinic/users は requireUser（clinicId スコープ）に緩和済み。
    const fetchClinicUsers = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/clinic/users'), {
          method: 'GET',
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setClinicUsers(data.users || []);
        }
      } catch {
        // エラーは静かに処理
      }
    };

    fetchClinicUsers();
  }, []);

  useEffect(() => {
    // モジュール起動可否・ライセンスステータス表示用。読取は権限不問（同一クリニックの全ロール）。
    const fetchContracts = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/clinic/license-contracts'), freshFetchInit({
          method: 'GET',
        }));
        if (response.ok) {
          const data = await response.json();
          setContracts(data.contracts || []);
        }
      } catch {
        // エラーは静かに処理
      }
    };

    fetchContracts();
  }, []);

  const activeLicenseKeys = useMemo(() => buildActiveLicenseKeySet(licenses), [licenses]);
  const hasAllLicense = activeLicenseKeys.has('all');

  const isModuleActive = (product) => isProductLicensed(product, activeLicenseKeys, hasAllLicense);

  const getProductContract = (product) => findContractForProduct(product, contracts);

  const isModuleLaunchable = (product) => {
    const contract = getProductContract(product);
    return isContractLaunchable(contract);
  };

  const allActiveProducts = useMemo(
    () => products.filter(isModuleActive),
    [products, activeLicenseKeys, hasAllLicense]
  );

  const launchableProducts = useMemo(
    () => allActiveProducts.filter(isModuleLaunchable),
    [allActiveProducts, contracts, hasPaymentMethod]
  );

  const expiredProducts = useMemo(
    () => allActiveProducts.filter((p) => !isModuleLaunchable(p)),
    [allActiveProducts, contracts, hasPaymentMethod]
  );

  const getLicenseStatus = () => {
    if (contracts.length === 0 && allActiveProducts.length === 0) {
      return { status: 'none', label: 'なし', date: null, dateLabel: null };
    }

    const primary = pickPrimaryContractSummary(contracts);
    if (primary) {
      return {
        status: primary.status,
        label: primary.label,
        date: primary.date,
        dateLabel: primary.dateLabel,
      };
    }

    if (allActiveProducts.length > 0) {
      const nextBillingDate = getNextBillingAnchorDate();
      return {
        status: 'active',
        label: 'サブスクリプション有効',
        date: nextBillingDate,
        dateLabel: '次回更新日',
      };
    }

    return { status: 'none', label: 'なし', date: null, dateLabel: null };
  };

  const licenseStatus = getLicenseStatus();

  const expiredModuleBanner = useMemo(() => {
    if (!canManageLicenses) {
      return {
        message: '試用期間が終了したモジュールがあります。継続利用については医師または管理者にご確認ください。',
        linkTo: '/licenses',
        linkLabel: 'ライセンスを確認',
      };
    }
    if (hasPaymentMethod) {
      return {
        message: '試用期間が終了したモジュールがあります。ライセンス管理ページから再契約してください。',
        linkTo: '/licenses',
        linkLabel: '再契約する',
      };
    }
    return {
      message: '試用期間が終了したモジュールがあります。引き続きご利用いただくには、支払い方法を登録してください。',
      linkTo: '/billing',
      linkLabel: '支払い方法を登録',
    };
  }, [canManageLicenses, hasPaymentMethod]);

  const handleLaunch = (url) => {
    if (!url || url === '#' || (typeof url === 'string' && url.trim() === '')) return;
    if (onLaunchStart) onLaunchStart(url);
  };

  const getLaunchUrl = (product) => getEffectiveLaunchUrl(product) ?? product?.launchUrl ?? '';
  const isLaunchUrlValid = (url) => url && url !== '#' && (typeof url !== 'string' || url.trim() !== '');

  const DASHBOARD_PANEL_TOTAL = 4;
  const launchWrapperClass = `hub-page-launch-wrapper${isLaunching ? ' is-launching' : ''}`;

  return (
    <>
      <div className={launchWrapperClass}>
      <div className="hub-main-header">
        <h1 className="hub-page-title">ダッシュボード</h1>
        <div style={{ display: 'flex', gap: 'var(--hub-space-md)' }}>
          <Link to="/modules" className="hub-btn hub-btn-secondary">
            モジュールを追加
          </Link>
        </div>
      </div>

      <div className="hub-main-content">
        <div
          className="hub-launch-panel"
          data-panel-index={3}
          data-panel-total={DASHBOARD_PANEL_TOTAL}
        >
        <div
          className="hub-card animate-fade-in"
          style={{
            background: 'var(--hub-bg-secondary)',
            marginBottom: 'var(--hub-space-2xl)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{
                fontFamily: 'var(--hub-font-display)',
                fontSize: '1.5rem',
                fontWeight: 700,
                marginBottom: 'var(--hub-space-sm)',
                color: 'var(--hub-text-primary)'
              }}>
                ようこそ、{clinic?.name || 'クリニック'} {user?.displayName || user?.userId || ''} 様
              </h2>
              <p style={{ color: 'var(--hub-text-secondary)', margin: 0 }}>
                Synapseで歯科医療のDXを推進しましょう
              </p>
            </div>
            {clinic?.logoUrl ? (
              <div style={{
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src={clinic.logoUrl}
                  alt={clinic.name}
                  style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: 'var(--hub-radius-md)' }}
                />
              </div>
            ) : null}
          </div>
        </div>
        </div>

        <div
          className="hub-launch-panel"
          data-panel-index={2}
          data-panel-total={DASHBOARD_PANEL_TOTAL}
        >
        <div className="hub-stats-grid">
          <div className="hub-stat-card animate-fade-in-up stagger-1">
            <div className="hub-stat-icon primary">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
            </div>
            <div className="hub-stat-body">
              <div className="hub-stat-label">利用中のモジュール</div>
              <div className="hub-stat-value" style={{ fontSize: '1.5rem' }}>{launchableProducts.length}</div>
            </div>
          </div>

          <div className="hub-stat-card animate-fade-in-up stagger-2">
            <div className="hub-stat-icon success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div className="hub-stat-body">
              <div className="hub-stat-label">登録ユーザー数</div>
              <div className="hub-stat-value" style={{ fontSize: '1.5rem' }}>{clinicUsers.length}名</div>
            </div>
          </div>

          <div className="hub-stat-card animate-fade-in-up stagger-3">
            <div className="hub-stat-icon warning">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div className="hub-stat-body">
              <div className="hub-stat-label">
                {licenseStatus.date ? (
                  <>
                    {licenseStatus.dateLabel}：{licenseStatus.date.getMonth() + 1}/{licenseStatus.date.getDate()}
                  </>
                ) : licenseStatus.dateLabel ? (
                  licenseStatus.dateLabel
                ) : (
                  'ライセンスステータス'
                )}
              </div>
              <div
                className="hub-stat-value"
                style={{
                  fontSize: '1.5rem',
                  color: licenseStatus.status === 'past_due' ? '#b91c1c' : undefined,
                }}
              >
                {licenseStatus.label}
              </div>
              {licenseStatus.status === 'past_due' && !betaFeaturesRestricted && canManageLicenses && (
                <Link to="/billing" className="hub-btn hub-btn-secondary" style={{ marginTop: '8px', fontSize: '0.8125rem' }}>
                  決済・請求を確認
                </Link>
              )}
            </div>
          </div>
        </div>
        </div>

        <div
          className="hub-launch-panel"
          data-panel-index={1}
          data-panel-total={DASHBOARD_PANEL_TOTAL}
        >
        <div className="animate-fade-in-up stagger-4" style={{ marginTop: 'var(--hub-space-2xl)' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--hub-space-lg)'
          }}>
            <h2 style={{
              fontFamily: 'var(--hub-font-display)',
              fontSize: '1.25rem',
              fontWeight: 700
            }}>
              利用中のモジュール
            </h2>
            <Link to="/modules" style={{
              color: 'var(--hub-accent-primary)',
              fontSize: '0.875rem',
              fontWeight: 600,
              textDecoration: 'none'
            }}>
              すべて見る →
            </Link>
          </div>

          {productsLoading ? (
            <div className="hub-card" style={{ textAlign: 'center', padding: 'var(--hub-space-2xl)' }}>
              <p style={{ color: 'var(--hub-text-secondary)', margin: 0 }}>
                読み込み中...
              </p>
            </div>
          ) : launchableProducts.length === 0 && expiredProducts.length === 0 ? (
            <div className="hub-card" style={{ textAlign: 'center', padding: 'var(--hub-space-3xl)' }}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{
                  width: 48,
                  height: 48,
                  color: 'var(--hub-text-muted)',
                  marginBottom: 'var(--hub-space-md)'
                }}
              >
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
              <h3 style={{
                fontWeight: 600,
                marginBottom: 'var(--hub-space-sm)',
                color: 'var(--hub-text-primary)'
              }}>
                モジュールがありません
              </h3>
              <p style={{
                color: 'var(--hub-text-secondary)',
                marginBottom: 'var(--hub-space-lg)'
              }}>
                モジュールを追加して、Synapseの機能をご利用ください。
              </p>
              <Link to="/modules" className="hub-btn hub-btn-primary">
                モジュールを追加する
              </Link>
            </div>
          ) : (
            <>
              {expiredProducts.length > 0 && (
                <div
                  className="hub-card hub-alert-banner"
                  style={{
                    background: 'rgba(245, 158, 11, 0.08)',
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                    marginBottom: 'var(--hub-space-lg)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--hub-space-md)'
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#b45309"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    style={{ width: 20, height: 20, flexShrink: 0, overflow: 'visible' }}
                  >
                    <circle cx="12" cy="12" r="9"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <circle cx="12" cy="16" r="1.1" fill="#b45309" stroke="none"/>
                  </svg>
                  <div style={{ flex: 1, fontSize: '0.875rem', color: 'var(--hub-text-primary)', lineHeight: 1.5 }}>
                    {expiredModuleBanner.message}
                  </div>
                  <Link
                    to={expiredModuleBanner.linkTo}
                    className="hub-btn hub-btn-primary"
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {expiredModuleBanner.linkLabel}
                  </Link>
                </div>
              )}

              {launchableProducts.length === 0 ? (
                <div className="hub-card" style={{ textAlign: 'center', padding: 'var(--hub-space-2xl)' }}>
                  <p style={{ color: 'var(--hub-text-secondary)', margin: 0 }}>
                    起動可能なモジュールはありません。
                  </p>
                  <Link to="/licenses" className="hub-btn hub-btn-primary" style={{ marginTop: 'var(--hub-space-md)', display: 'inline-block' }}>
                    ライセンスを確認
                  </Link>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 'var(--hub-space-lg)'
                }}>
                  {launchableProducts.map((product, index) => {
                    const launchUrl = getLaunchUrl(product);
                    const description = product.shortDescription || product.description || '';

                    return (
                      <div
                        key={product.id}
                        className="hub-card hub-card-elevated animate-fade-in-up"
                        style={{
                          animationDelay: `${(index + 1) * 0.1}s`,
                          display: 'flex',
                          flexDirection: 'column',
                          minHeight: '180px'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontWeight: 700,
                            marginBottom: 'var(--hub-space-sm)'
                          }}>
                            {product.name}
                          </h3>
                          <p style={{
                            color: 'var(--hub-text-secondary)',
                            fontSize: '0.875rem',
                            margin: 0,
                            lineHeight: 1.5
                          }}>
                            {description}
                          </p>
                        </div>

                        <div style={{ marginTop: 'auto', paddingTop: 'var(--hub-space-lg)' }}>
                          <button
                            type="button"
                            onClick={() => handleLaunch(launchUrl)}
                            className="hub-btn hub-btn-primary"
                            style={{ width: '100%' }}
                            disabled={!isLaunchUrlValid(launchUrl)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polygon points="5 3 19 12 5 21 5 3"/>
                            </svg>
                            起動
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
        </div>

        <div
          className="hub-launch-panel"
          data-panel-index={0}
          data-panel-total={DASHBOARD_PANEL_TOTAL}
        >
        <div className="animate-fade-in-up stagger-5" style={{ marginTop: 'var(--hub-space-2xl)' }}>
          <h2 style={{
            fontFamily: 'var(--hub-font-display)',
            fontSize: '1.25rem',
            fontWeight: 700,
            marginBottom: 'var(--hub-space-lg)'
          }}>
            クイックアクション
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--hub-space-md)'
          }}>
            <Link
              to="/settings/users"
              className="hub-card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--hub-space-md)',
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                background: 'var(--hub-accent-primary-light)',
                borderRadius: 'var(--hub-radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--hub-accent-primary)'
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 22, height: 22 }}>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="8.5" cy="7" r="4"/>
                  <line x1="20" y1="8" x2="20" y2="14"/>
                  <line x1="23" y1="11" x2="17" y2="11"/>
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>ユーザーを追加</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)' }}>
                  新しいスタッフを追加
                </div>
              </div>
            </Link>

            <Link
              to="/settings/clinic"
              className="hub-card"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--hub-space-md)',
                textDecoration: 'none',
                color: 'inherit'
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                background: 'var(--hub-accent-primary-light)',
                borderRadius: 'var(--hub-radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--hub-accent-primary)'
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 22, height: 22 }}>
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>クリニック設定</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)' }}>
                  情報を編集
                </div>
              </div>
            </Link>

            {betaFeaturesRestricted ? (
              <div
                className="hub-card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--hub-space-md)',
                  color: 'inherit',
                  opacity: 0.6,
                  pointerEvents: 'none',
                  cursor: 'not-allowed'
                }}
                title="β版期間中はご利用いただけません"
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  background: 'var(--hub-accent-primary-light)',
                  borderRadius: 'var(--hub-radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--hub-accent-primary)'
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 22, height: 22 }}>
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>請求管理</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)' }}>
                    支払い情報を確認
                  </div>
                </div>
              </div>
            ) : (
              <Link
                to="/billing"
                className="hub-card"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--hub-space-md)',
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  background: 'var(--hub-accent-primary-light)',
                  borderRadius: 'var(--hub-radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--hub-accent-primary)'
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 22, height: 22 }}>
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>請求管理</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--hub-text-muted)' }}>
                    支払い情報を確認
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Dashboard;
